import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Guid from "_shared/guids/guid";
import MarketplaceTeam from "marketplace/teams/team";
import EntityMember from "_shared/entity-members/entity-member";
import { JSONType } from "work/proposals/redlining/field-redlines/field-redline";
import { VendorTeamJSON, VendorTeamParams } from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";

export default class VendorTeam {
  public static get EXAMPLE(): VendorTeam {
    return new VendorTeam({
      leader: EntityMember.EXAMPLE,
      members: [EntityMember.EXAMPLE],
      marketplaceId: Guid.generate(),
    });
  }

  public static fromJSON(json: VendorTeamJSON | null | undefined): VendorTeam | null | undefined {
    if (!json) return json;

    return new VendorTeam({
      leader: EntityMember.fromJSON(json.leader) ?? undefined,
      members:
        json.members?.map((m) => {
          const member = EntityMember.fromJSON(m);
          if (!member) throw new Error("Invalid member in VendorTeam JSON");
          return member;
        }) ?? [],
      marketplaceId: Guid.fromJSON(json.marketplaceId) ?? undefined,
    });
  }

  public static fromUnknownJSON(json: JSONType): VendorTeam | null | undefined {
    if (isNullOrUndefined(json)) return json;
    else if (isValidTeamJSON(json, { throw: true })) return VendorTeam.fromJSON(json);
    throw new Error("Cannot create VendorTeam from invalid JSON");
  }

  public static FromMarketplaceTeam(marketplaceTeam: MarketplaceTeam): VendorTeam | undefined {
    if (!marketplaceTeam) return undefined;
    return new VendorTeam({
      leader: marketplaceTeam.leader.toEntityMember(),
      members: marketplaceTeam.members.map((m) => m.toEntityMember()),
      marketplaceId: marketplaceTeam.id,
    });
  }

  public readonly leader?: EntityMember;
  public readonly marketplaceId?: Guid;

  public get members(): EntityMember[] {
    return [...this._members];
  } // Return a copy to prevent external mutation

  /** Those of this team's members that cannot be seated because its leader is the same person. */
  public get membersDisplacedByLeader(): EntityMember[] {
    return this._members.filter((member) => this.isLedBy(member));
  }

  public constructor(params: VendorTeamParams) {
    Object.assign(this, { ...params });
  }

  public isEqualTo(other?: unknown): boolean {
    return (
      other instanceof VendorTeam &&
      ((!this.leader && !other.leader) || this.leader?.isEqualTo(other.leader) || false) &&
      this.members.length === other.members.length &&
      this.members.every((member) => other.members.some((otherMember) => member.isEqualTo(otherMember))) &&
      ((!this.marketplaceId && !other.marketplaceId) || this.marketplaceId?.isEqualTo(other.marketplaceId) || false)
    );
  }

  /**
   * Whether `member` is the person leading this team.
   *
   * A person leads a vendor team or belongs to it, never both. The rule is the domain's, not the
   * UI's: `VendorTeam.ValidateInputAsync` rejects a team whose leader appears among its members with
   * an `InvalidVendorTeamLeaderException`, and that validation runs on the negotiation save path for
   * any `/vendorTeam/*` touch. Every place that can seat a leader or add a member therefore has to
   * settle the collision BEFORE it builds the team, or the user's edit comes back as an opaque 400
   * with nothing to act on (#4491).
   *
   * The domain compares people by user id alone — the same person under a second legal entity is
   * still the same person — so this asks `EntityMember.isSamePersonAs` rather than `isEqualTo`.
   */
  public isLedBy(member: EntityMember | null | undefined): boolean {
    return this.leader?.isSamePersonAs(member) ?? false;
  }

  /**
   * This team with whoever leads it taken out of its member list. Leadership wins: a person named as
   * leader is already on the team, so listing them again adds nothing and costs the save.
   */
  public withLeaderRemovedFromMembers(): VendorTeam {
    return new VendorTeam({
      leader: this.leader,
      members: this._members.filter((member) => !this.isLedBy(member)),
      marketplaceId: this.marketplaceId,
    });
  }

  public toJSON(): VendorTeamJSON {
    return {
      leader: this.leader?.toJSON(),
      members: this._members.map((m) => m.toJSON()),
      marketplaceId: this.marketplaceId?.toJSON(),
    };
  }

  // Overrides replace only the fields they name — every other field, `marketplaceId` included,
  // carries over from this team. Rebuilding a team from scratch at each edit site (`new
  // VendorTeam({ leader, members })`) is how a marketplace-sourced team quietly stopped being one
  // partway through drafting (#4515): the caller only ever named the two fields it meant to change,
  // so anything else the team was carrying got silently dropped.
  public clone(overrides: Partial<VendorTeamParams> = {}): VendorTeam {
    return new VendorTeam({
      leader: this.leader ? this.leader.clone() : undefined,
      members: this._members.map((m) => m.clone()),
      marketplaceId: this.marketplaceId ? this.marketplaceId.clone() : undefined,
      ...overrides,
    });
  }

  private _members: EntityMember[] = [];

  private set members(members: EntityMember[]) {
    this._members = members;
  }
}

export class InvalidVendorTeamJSON extends Error {
  public readonly json: JSONType;
  public readonly message: string = "Invalid VendorTeamJSON";

  constructor(args: { json: JSONType; message?: string }) {
    super(`${args.message}: ${JSON.stringify(args.json)}`);
    this.json = args.json;
  }
}

function isValidTeamJSON(json: JSONType, options?: { throw: boolean }): json is VendorTeamJSON {
  options = options ?? { throw: false };
  if (isNullOrUndefined(json)) return true;

  if (typeof json !== "object" || json === null) {
    if (options.throw) throw new InvalidVendorTeamJSON({ json });
    return false;
  }

  if ("leader" in json && !isNullOrUndefined(json.leader)) {
    if (typeof json.leader !== "object") {
      if (options.throw)
        throw new InvalidVendorTeamJSON({
          json,
          message: "Invalid VendorTeamJSON leader",
        });
      return false;
    }
  }

  if (!("members" in json) || !Array.isArray(json.members)) {
    if (options.throw)
      throw new InvalidVendorTeamJSON({
        json,
        message: "Invalid VendorTeamJSON members",
      });
    return false;
  }

  return true;
}
